import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './slices/store';
// Self-hosted Inter (variable) — primary UI font, consistent across all OSes.
// Both axes share family 'Inter Variable'; italic is used for accent words.
import '@fontsource-variable/inter';
import '@fontsource-variable/inter/wght-italic.css';
// Self-hosted monospace (numbers, ranking heatmap, code) — consistent across OSes.
import '@fontsource-variable/jetbrains-mono';
import './index.css';
// Set Chart.js canvas font to Inter (single source of truth).
import './utils/chartDefaults';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'react-toastify/dist/ReactToastify.css';
import { initSentry, Sentry } from './utils/sentry';

// Initialise Sentry once before the React tree mounts. The Redux enhancer in
// store.js reads the hub lazily (per-action), so it's fine that the store
// module loaded before this call ran.
initSentry();

// Helper to apply the 'dark' class on the <html> element based on theme
function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // system
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    const isDark = mq ? mq.matches : false;
    if (isDark) root.classList.add('dark'); else root.classList.remove('dark');
  }
}

// Read initial theme from the Redux store (preferences slice)
const initialTheme = store.getState()?.preferences?.theme || 'system';
applyTheme(initialTheme);

// Subscribe to store to react to theme changes
let previousTheme = initialTheme;
store.subscribe(() => {
  try {
    const state = store.getState();
    const theme = state?.preferences?.theme || 'system';
    if (theme !== previousTheme) {
      applyTheme(theme);
      previousTheme = theme;
    }
  } catch (err) {
    // ignore
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div style={{ padding: 24, fontFamily: 'sans-serif' }}>
        <h1>Something went wrong</h1>
        <p>The page hit an unexpected error and has been reported.</p>
        <pre style={{ background: '#f4f4f5', padding: 12, overflow: 'auto' }}>
          {error?.message || String(error)}
        </pre>
        <button onClick={resetError}>Try again</button>
      </div>
    )}
  >
    <Provider store={store}>
      <App />
    </Provider>
  </Sentry.ErrorBoundary>
);

reportWebVitals();
